const SCRIPT_ID = "gmaps-script";
export const GOOGLE_MAPS_AUTH_ERROR = "Google Maps rechazó la configuración de la API. Revisa facturación, Maps JavaScript API y las restricciones de la clave.";

export const isGoogleMapsReady = (windowObject = window) => Boolean(
  windowObject?.google?.maps?.places && windowObject?.google?.maps?.Geocoder
);

const installAuthFailureHandler = windowObject => {
  if (!windowObject || windowObject.__venaqqiMapsAuthHookInstalled) return;
  const previousAuthFailure = windowObject.gm_authFailure;
  windowObject.__venaqqiMapsAuthHookInstalled = true;
  windowObject.gm_authFailure = () => {
    const error = new Error(GOOGLE_MAPS_AUTH_ERROR);
    windowObject.__venaqqiMapsAuthError = error;
    try { previousAuthFailure?.(); } catch { /* preserve Venaqqi handling */ }
    try {
      const EventCtor = windowObject.CustomEvent;
      if (EventCtor && windowObject.dispatchEvent) {
        windowObject.dispatchEvent(new EventCtor("venaqqi:google-maps-auth-error", { detail: { message: error.message } }));
      }
    } catch { /* custom events may be unavailable in tests */ }
  };
};

export const loadGoogleMaps = (onReady, onError, options = {}) => {
  const windowObject = options.windowObject || window;
  const documentObject = options.documentObject || document;
  const apiKey = options.apiKey ?? process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const timeoutMs = options.timeoutMs || 6000;
  let readyCalled = false;
  let failed = false;
  let timeoutTimer;
  let authGraceTimer;

  installAuthFailureHandler(windowObject);

  const cleanupAuthListener = () => {
    if (authGraceTimer) clearTimeout(authGraceTimer);
    windowObject.removeEventListener?.("venaqqi:google-maps-auth-error", onAuthError);
  };
  const fail = value => {
    if (failed) return;
    failed = true;
    if (timeoutTimer) clearTimeout(timeoutTimer);
    cleanupAuthListener();
    const error = value instanceof Error ? value : new Error(value?.detail?.message || GOOGLE_MAPS_AUTH_ERROR);
    onError?.(error);
  };
  const ready = value => {
    if (readyCalled || failed) return;
    readyCalled = true;
    if (timeoutTimer) clearTimeout(timeoutTimer);
    onReady?.(value);
    authGraceTimer = setTimeout(cleanupAuthListener, 2500);
  };
  function onAuthError(event) {
    fail(new Error(event?.detail?.message || GOOGLE_MAPS_AUTH_ERROR));
  }

  windowObject.addEventListener?.("venaqqi:google-maps-auth-error", onAuthError);

  if (windowObject.__venaqqiMapsAuthError) {
    fail(windowObject.__venaqqiMapsAuthError);
    return;
  }
  if (isGoogleMapsReady(windowObject)) {
    ready(windowObject.google);
    return;
  }
  if (!apiKey) {
    fail(new Error("Google Maps no está configurado."));
    return;
  }

  const waitUntilReady = () => {
    if (windowObject.__venaqqiMapsAuthError) {
      fail(windowObject.__venaqqiMapsAuthError);
      return;
    }
    if (isGoogleMapsReady(windowObject)) ready(windowObject.google);
  };

  const existing = documentObject.getElementById(SCRIPT_ID);
  if (existing) {
    existing.addEventListener?.("load", waitUntilReady, { once: true });
    existing.addEventListener?.("error", () => fail(new Error("No se pudo cargar Google Maps.")), { once: true });
  } else {
    const script = documentObject.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&language=es`;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", waitUntilReady, { once: true });
    script.addEventListener("error", () => {
      script.remove();
      fail(new Error("No se pudo cargar Google Maps."));
    }, { once: true });
    documentObject.head.appendChild(script);
  }

  const poll = () => {
    if (failed || readyCalled) return;
    if (windowObject.__venaqqiMapsAuthError) {
      fail(windowObject.__venaqqiMapsAuthError);
      return;
    }
    if (isGoogleMapsReady(windowObject)) {
      ready(windowObject.google);
      return;
    }
    setTimeout(poll, 100);
  };

  timeoutTimer = setTimeout(() => fail(new Error("Google Maps tardó demasiado en responder.")), timeoutMs);
  poll();
};

export const loadGoogleMapsPromise = options => new Promise((resolve, reject) => {
  loadGoogleMaps(resolve, reject, options);
});
